import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import { FONT_MONO } from "../../theme/v2Tokens";
import type Interaction from "../../../types/interaction/Interaction";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(ms?: number): string {
  if (!ms) return "\u2014";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatTimestamp(value?: string | null): string {
  if (!value) return "\u2014";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatRetentionDate(value?: number | null): string {
  if (value == null) return "\u2014";
  try {
    // retentionDate is Unix seconds (see Interaction.ts); Date expects ms.
    return new Date(value * 1000).toLocaleString();
  } catch {
    return String(value);
  }
}

// ---------------------------------------------------------------------------
// Type badge config
// ---------------------------------------------------------------------------

const TYPE_BADGE_STYLES: Record<string, { label: string; bg: string; color: string }> = {
  Audio: { label: "Audio", bg: "rgba(2,136,181,0.20)", color: "var(--signal-blue)" },
  Video: { label: "Video", bg: "rgba(143,153,251,0.35)", color: "var(--badge-violet-text)" },
  AudioAndVideo: { label: "Video", bg: "rgba(143,153,251,0.35)", color: "var(--badge-violet-text)" },
  Chat: { label: "Chat", bg: "rgba(249,115,22,0.20)", color: "var(--signal-amber)" },
  Email: { label: "Email", bg: "rgba(79,169,71,0.25)", color: "var(--signal-emerald)" },
};

// ---------------------------------------------------------------------------
// Column definition type
// ---------------------------------------------------------------------------

export interface ColumnRenderContext {
  siteMap?: Map<string, string>; // siteId → site name
  viewMap?: Map<string, string>; // viewId → view name
  t?: TFunction;
}

export interface ColumnDef {
  field: string;
  label: string;
  render: (row: Interaction, ctx?: ColumnRenderContext) => React.ReactNode;
  mono?: boolean;
}

// ---------------------------------------------------------------------------
// Default label mapping (same as V1's SearchFieldLabelProvider)
// ---------------------------------------------------------------------------

export const DEFAULT_LABEL_MAPPING: Record<string, string> = {
  interactionType: "Type",
  agentName: "Agent",
  agentId: "Agent ID",
  startTime: "Start Time",
  endTime: "End Time",
  durationMs: "Duration",
  siteId: "Site",
  siteIds: "Sites",
  phoneNumber: "Phone Number",
  dnis: "DNIS",
  extension: "Extension",
  direction: "Direction",
  device: "Device",
  group: "Group",
  hasMedia: "Has Media",
  hasTranscription: "Has Transcript",
  skill: "Skill",
  sourceId: "Source ID",
  callId: "Call ID",
  interactionId: "Interaction ID",
  masterInteractionId: "Master ID",
  participants: "Participants",
  segmentId: "Segment ID",
  segmentIds: "Segment IDs",
  retentionDate: "Retention Date",
  legalHold: "Legal Hold",
  tags: "Tags",
  views: "Views",
};

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export const COLUMN_DEFS: ColumnDef[] = [
  {
    field: "interactionType",
    label: DEFAULT_LABEL_MAPPING.interactionType,
    render: (row) => {
      const badge = TYPE_BADGE_STYLES[row.interactionType] ?? {
        label: row.interactionType,
        bg: "var(--border-mid)",
        color: "var(--text-secondary)",
      };
      return (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            px: "8px",
            py: "2px",
            fontSize: "0.6875rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            backgroundColor: badge.bg,
            color: badge.color,
            borderRadius: "20px",
          }}
        >
          {badge.label}
        </Box>
      );
    },
  },
  {
    field: "agentName",
    label: DEFAULT_LABEL_MAPPING.agentName,
    render: (row) => (
      <Box component="span" sx={{ fontWeight: 600, color: "var(--text-primary)" }}>
        {row.agentName ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "startTime",
    label: DEFAULT_LABEL_MAPPING.startTime,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {formatTimestamp(row.startTime)}
      </Box>
    ),
  },
  {
    field: "endTime",
    label: DEFAULT_LABEL_MAPPING.endTime,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {formatTimestamp(row.endTime)}
      </Box>
    ),
  },
  {
    field: "durationMs",
    label: DEFAULT_LABEL_MAPPING.durationMs,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-tertiary)" }}>
        {formatDuration(row.durationMs)}
      </Box>
    ),
  },
  {
    field: "siteId",
    label: DEFAULT_LABEL_MAPPING.siteId,
    render: (row, ctx) => {
      const name = ctx?.siteMap?.get(row.siteId) ?? row.siteId;
      return (
        <Box component="span" sx={{ color: "var(--text-secondary)" }}>
          {name || "\u2014"}
        </Box>
      );
    },
  },
  {
    field: "phoneNumber",
    label: DEFAULT_LABEL_MAPPING.phoneNumber,
    mono: true,
    render: (row) => (
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          color: row.phoneNumber ? "var(--text-secondary)" : "var(--text-muted)",
        }}
      >
        {row.phoneNumber ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "dnis",
    label: DEFAULT_LABEL_MAPPING.dnis,
    mono: true,
    render: (row) => (
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          color: row.dnis ? "var(--text-secondary)" : "var(--text-muted)",
        }}
      >
        {row.dnis ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "extension",
    label: DEFAULT_LABEL_MAPPING.extension,
    render: (row) => (
      <Box component="span" sx={{ color: "var(--text-secondary)" }}>
        {row.extension ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "direction",
    label: DEFAULT_LABEL_MAPPING.direction,
    render: (row) => (
      <Box component="span" sx={{ color: "var(--text-secondary)" }}>
        {row.direction ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "device",
    label: DEFAULT_LABEL_MAPPING.device,
    render: (row) => (
      <Box component="span" sx={{ color: "var(--text-secondary)" }}>
        {row.device ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "group",
    label: DEFAULT_LABEL_MAPPING.group,
    render: (row) => (
      <Box component="span" sx={{ color: "var(--text-secondary)" }}>
        {row.group ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "skill",
    label: DEFAULT_LABEL_MAPPING.skill,
    render: (row) => (
      <Box component="span" sx={{ color: "var(--text-secondary)" }}>
        {"\u2014"}
      </Box>
    ),
  },
  {
    field: "sourceId",
    label: DEFAULT_LABEL_MAPPING.sourceId,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {row.sourceId ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "callId",
    label: DEFAULT_LABEL_MAPPING.callId,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {row.callId ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "interactionId",
    label: DEFAULT_LABEL_MAPPING.interactionId,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {row.interactionId}
      </Box>
    ),
  },
  {
    field: "masterInteractionId",
    label: DEFAULT_LABEL_MAPPING.masterInteractionId,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {row.masterInteractionId ?? "\u2014"}
      </Box>
    ),
  },
  {
    field: "participants",
    label: DEFAULT_LABEL_MAPPING.participants,
    render: (row) => (
      <Box component="span" sx={{ color: "var(--text-secondary)" }}>
        {row.participants && row.participants.length > 0
          ? row.participants.join(", ")
          : "\u2014"}
      </Box>
    ),
  },
  {
    field: "segmentIds",
    label: DEFAULT_LABEL_MAPPING.segmentIds,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {row.segmentIds && row.segmentIds.length > 0
          ? row.segmentIds.join(", ")
          : "\u2014"}
      </Box>
    ),
  },
  {
    field: "retentionDate",
    label: DEFAULT_LABEL_MAPPING.retentionDate,
    mono: true,
    render: (row) => (
      <Box component="span" sx={{ fontFamily: FONT_MONO, color: "var(--text-secondary)" }}>
        {formatRetentionDate(row.retentionDate)}
      </Box>
    ),
  },
  {
    field: "legalHold",
    label: DEFAULT_LABEL_MAPPING.legalHold,
    render: (row, ctx) => {
      const yesLabel = ctx?.t ? String(ctx.t("common:yes")) : "Yes";
      const noLabel = ctx?.t ? String(ctx.t("common:no")) : "No";
      return (
        <Box
          component="span"
          sx={{
            color: row.legalHold ? "var(--signal-amber)" : "var(--text-tertiary)",
            fontWeight: row.legalHold ? 600 : 400,
          }}
        >
          {row.legalHold ? yesLabel : noLabel}
        </Box>
      );
    },
  },
  {
    field: "tags",
    label: DEFAULT_LABEL_MAPPING.tags,
    render: (row) => (
      <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {row.tags && row.tags.length > 0 ? (
          row.tags.map((tag) => (
            <Box
              key={tag}
              component="span"
              sx={{
                display: "inline-block",
                px: "7px",
                py: "1px",
                fontSize: "0.6875rem",
                fontWeight: 500,
                backgroundColor: "var(--bg-raised)",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-mid)",
                borderRadius: "12px",
              }}
            >
              {tag}
            </Box>
          ))
        ) : (
          <Box component="span" sx={{ color: "var(--text-muted)" }}>
            {"\u2014"}
          </Box>
        )}
      </Box>
    ),
  },
  {
    field: "views",
    label: DEFAULT_LABEL_MAPPING.views,
    render: (row, ctx) => (
      <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {row.views && row.views.length > 0 ? (
          row.views.map((view) => (
            <Box
              key={view}
              component="span"
              sx={{
                display: "inline-block",
                px: "7px",
                py: "1px",
                fontSize: "0.6875rem",
                fontWeight: 500,
                backgroundColor: "var(--bg-raised)",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-mid)",
                borderRadius: "12px",
              }}
            >
              {ctx?.viewMap?.get(view) ?? view}
            </Box>
          ))
        ) : (
          <Box component="span" sx={{ color: "var(--text-muted)" }}>
            {"\u2014"}
          </Box>
        )}
      </Box>
    ),
  },
];
